import { create } from 'zustand';

import { AIAssistantState, AIRecommendation } from '@/apps/AIAssistant/types';
import {
  loadRecommendationsFromStorage,
  saveRecommendationsToStorage,
} from '@/apps/AIAssistant/utils';
import { generateAIRecommendationsViaSupabase } from '@/apps/AIAssistant/utils/supabaseAIUtils';
import { useChecklistStore } from '@/apps/Checklist/stores/useChecklistStore';
import { useScheduleStore } from '@/apps/Schedule/stores/useScheduleStore';
import { useWidgetStore } from '@/common/stores/useWidgetStore';
import handleException from '@/common/utils/errors';

const initialState = loadRecommendationsFromStorage();

export const useAIAssistantStore = create<AIAssistantState>((set, get) => ({
  recommendations: initialState.recommendations,
  loading: false,
  error: null,
  lastUpdated: initialState.lastUpdated,

  loadFromLocalStorage: () => {
    const { recommendations, lastUpdated } = loadRecommendationsFromStorage();
    set({ recommendations, lastUpdated });
  },

  generateRecommendations: async () => {
    set({ loading: true, error: null });
    try {
      const scheduleStore = useScheduleStore.getState();

      if (scheduleStore.loading) {
        await new Promise<void>((resolve) => {
          const unsubscribe = useScheduleStore.subscribe((state) => {
            if (!state.loading) {
              unsubscribe();
              resolve();
            }
          });
        });
      }

      const googleCalendarEvents = useScheduleStore.getState().events || [];

      const recommendations = await generateAIRecommendationsViaSupabase(googleCalendarEvents);
      const lastUpdated = new Date().toISOString();

      set({
        recommendations,
        lastUpdated,
        loading: false,
      });

      saveRecommendationsToStorage(recommendations, lastUpdated);
    } catch (error) {
      handleException(error);
      set({
        error: error instanceof Error ? error.message : 'Failed to generate recommendations',
        loading: false,
      });
    }
  },

  clearRecommendations: () => {
    set({ recommendations: [], lastUpdated: null });
    saveRecommendationsToStorage([], null);
  },

  applyRecommendation: async (recommendation: AIRecommendation) => {
    try {
      if (recommendation.type === 'TASK') {
        const widgets = useWidgetStore.getState().widgets;
        const checklistWidget = widgets.find((w) => w.widget_id === 'checklist');

        if (!checklistWidget) {
          throw new Error('Checklist widget not found. Please add it to your dashboard first.');
        }

        await useChecklistStore.getState().addItem(checklistWidget.id, recommendation.content);
      }

      const updatedRecommendations = get().recommendations.filter(
        (r) => r.id !== recommendation.id,
      );

      set({ recommendations: updatedRecommendations });

      saveRecommendationsToStorage(updatedRecommendations, get().lastUpdated);
    } catch (error) {
      handleException(error);
      throw error;
    }
  },

  dismissRecommendation: (id: string) => {
    const updatedRecommendations = get().recommendations.filter(
      (recommendation) => recommendation.id !== id,
    );

    set({ recommendations: updatedRecommendations });
    saveRecommendationsToStorage(updatedRecommendations, get().lastUpdated);
  },
}));
